import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useDebounce } from "src/reusable/hooks/useDebounce";
import {
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from "src/features/globalSearch/constants/globalSearch.constants";
import { useConversationHistory } from "src/api/conversationHistory";
import type { ConversationHistoryItem } from "src/api/conversationHistory";
import ConversationHistoryResultsPanel from "./ConversationHistoryResultsPanel";
import { conversationPath } from "./conversationPath";

/**
 * Matches SEARCH_DEBOUNCE_MS in globalSearch: the endpoint behind this box is
 * a real BM25 query with an index-existence round trip per call, so it wants
 * the same throttling the app's other full-text surface uses.
 */
export const HISTORY_SEARCH_DEBOUNCE_MS = SEARCH_DEBOUNCE_MS;

interface ConversationHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

/** The two In projects / General scopes, as the endpoint's `in_project`. */
type HistoryScope = "projects" | "general";

/**
 * The searchable conversation-history surface (Library 1.33 §2.5, Story 16).
 * A scope tab (In projects / General) selects which group is listed; within
 * it, an empty query lists by recency and typing searches title + message
 * text as you type, both staying inside the selected scope. Each scope pages
 * independently — switching tabs doesn't disturb the other's position.
 */
export default function ConversationHistoryModal({
  open,
  onClose,
}: ConversationHistoryModalProps) {
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, HISTORY_SEARCH_DEBOUNCE_MS);
  const [scope, setScope] = useState<HistoryScope>("projects");

  // Two guards on what reaches the query:
  //  - `open ? … : ""` because this component never unmounts (the button
  //    renders it unconditionally), so `debouncedQuery` survives a close and
  //    would otherwise re-run the previous search behind an empty box on a
  //    quick reopen.
  //  - a single character is dropped by the backend anyway, so don't spend a
  //    request on it (MIN_SEARCH_LENGTH, as globalSearch does).
  const trimmed = debouncedQuery.trim();
  const effectiveQuery =
    open && trimmed.length >= MIN_SEARCH_LENGTH ? debouncedQuery : "";
  const inProject = scope === "projects";

  // Only for the search box's spinner and the pinned degraded-search note
  // below — the results themselves come from ConversationHistoryResultsPanel's
  // own (remount-keyed) call to the same hook; react-query shares the
  // underlying fetch/cache entry across both observers on the same key.
  const { isFetching, isLoading, searchBackend } = useConversationHistory(
    effectiveQuery,
    open,
    inProject,
  );

  // Focus the input when the dialog opens; the delay must exceed the dialog
  // animation (200ms) for focus to stick (same as GlobalSearchModal).
  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 250);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Start from the full recency listing, scoped to In projects, on each open.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setScope("projects");
    }
  }, [open]);

  const handleOpen = (item: ConversationHistoryItem) => {
    history.push(conversationPath(item));
    onClose();
  };

  const searching = effectiveQuery.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: {
          position: "fixed",
          top: "15%",
          borderRadius: 2,
          maxHeight: "70vh",
          overflow: "hidden",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
      data-testid="conversation-history-modal"
      data-attr="conversation-history:modal:history"
      aria-labelledby="conversation-history-title"
    >
      <Paper elevation={0}>
        <Box sx={{ p: 2 }}>
          <Typography
            id="conversation-history-title"
            variant="subtitle1"
            sx={{ mb: 1 }}
          >
            Conversation history
          </Typography>
          <TextField
            inputRef={inputRef}
            fullWidth
            placeholder="Search your conversations by title or what was said..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            autoFocus
            inputProps={{ "aria-label": "Search your conversation history" }}
            data-testid="conversation-history-search-input"
            data-attr="conversation-history:input:search-query"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              // isLoading is also isFetching on a cold open, which would
              // render this beside the body spinner below.
              endAdornment:
                isFetching && !isLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ) : undefined,
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Box>

        <Tabs
          value={scope}
          onChange={(_event, next: HistoryScope) => setScope(next)}
          aria-label="Conversation history scope"
          sx={{ px: 2, minHeight: 36 }}
          data-testid="conversation-history-scope-tabs"
        >
          <Tab
            value="projects"
            label="In projects"
            sx={{ minHeight: 36, py: 0 }}
            data-testid="conversation-history-scope-projects"
            data-attr="conversation-history:tab:in-projects"
          />
          <Tab
            value="general"
            label="General"
            sx={{ minHeight: 36, py: 0 }}
            data-testid="conversation-history-scope-general"
            data-attr="conversation-history:tab:general"
          />
        </Tabs>

        <DialogContent
          sx={{ p: 0, maxHeight: "calc(70vh - 176px)", overflow: "auto" }}
          dividers
        >
          <ConversationHistoryResultsPanel
            key={scope}
            searchQuery={effectiveQuery}
            enabled={open}
            inProject={inProject}
            onOpen={handleOpen}
            testIdPrefix="conversation-history"
            loadMoreErrorTestId="conversation-history-page-error"
            loadMoreDataAttr="conversation-history:btn:load-more"
            hideDegradedNote
          />
        </DialogContent>

        {/* The backend degrades to a title-only database filter when the
            full-text index is unavailable; say so instead of silently
            returning narrower results. */}
        {searchBackend === "database" && searching && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              color="text.disabled"
              data-testid="conversation-history-degraded-note"
            >
              Full-text search is unavailable; matching titles only.
            </Typography>
          </Box>
        )}
      </Paper>
    </Dialog>
  );
}
